import React, { useId, useMemo } from 'react';
import { FIRST_YEAR, LAST_YEAR, itemsInYears } from '../../data/archive';
interface YearsDemoProps {
  start: number;
  end: number;
  onChange: (span: {
    start: number;
    end: number;
  }) => void;
}
const DECADE_TICKS = [1976, 1986, 1996, 2006, 2016, 2026];

/**
 * The organising principle, demonstrated rather than asserted. Nothing is
 * asked and nothing is stored — the visitor sets a span and the page below
 * reorders around it.
 *
 * Built from two range inputs so it is keyboard-operable, works on touch, and
 * degrades to two plain sliders with visible values if styling fails. The
 * static first frame already shows a real span, so it carries meaning before
 * anyone touches it.
 */
export function YearsDemo({
  start,
  end,
  onChange
}: YearsDemoProps) {
  const id = useId();
  const span = LAST_YEAR - FIRST_YEAR;
  const items = useMemo(() => itemsInYears(start, end).length, [start, end]);
  const left = (start - FIRST_YEAR) / span * 100;
  const width = (end - start) / span * 100;
  return <div aria-labelledby={`${id}-title`} role="group" className="flex flex-col gap-5 rounded-card border border-rule bg-card p-5 shadow-card sm:p-6" data-id="element-173" data-mp="2gzjs76ze0o0l">
      <h3 id={`${id}-title`} className="sr-only" data-id="element-174" data-mp="27a9y5lkzgv9u">
        Set the years you were here
      </h3>

      {/* The axis. Decorative — the range inputs below carry the semantics. */}
      <div aria-hidden="true" className="flex flex-col gap-2" data-id="element-175" data-mp="1ya3okaseoffy">
        <div className="relative h-12" data-id="element-176" data-mp="1ybojw2mjo2ev">
          <div className="absolute inset-x-0 top-5 h-2 rounded-pill bg-canvas" data-id="element-177" data-mp="1tbk30bhq55o4" />
          <div className="absolute top-5 h-2 rounded-pill bg-primary" style={{
          left: `${left}%`,
          width: `${Math.max(width, 1)}%`
        }} data-id="element-178" data-mp="1oc7e7l7aloiw" />
          <div className="absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-pill bg-ink px-2.5 py-0.5 text-eyebrow-caption font-semibold text-on-ink" style={{
          left: `${Math.min(Math.max(left + width / 2, 8), 92)}%`
        }} data-id="element-179" data-mp="1jexgli9r26jz">
            {start}–{end}
          </div>
        </div>
        <ol className="flex justify-between text-eyebrow-caption text-ink-subtle" data-id="element-180" data-mp="1bzvbnkxx8ie6">
          {DECADE_TICKS.map(year => <li key={year} data-id="element-181" data-mp="12420o5zuowyi">{year}</li>)}
        </ol>
      </div>

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-182" data-mp="121xlgrwi4bix">
        <label className="flex flex-col gap-1.5" data-id="element-183" data-mp="173wr68fprkj1">
          <span className="text-small font-medium text-ink" data-id="element-184" data-mp="170ilaq7uct6u">
            First year <span className="text-ink-muted" data-id="element-185" data-mp="1usz8ff6frbd0">{start}</span>
          </span>
          <input type="range" min={FIRST_YEAR} max={LAST_YEAR} value={start} aria-label="First year you were here" onChange={event => {
          const next = Number(event.target.value);
          onChange({
            start: next,
            end: Math.max(next, end)
          });
        }} className="h-tap w-full accent-[color:var(--color-primary)]" data-id="element-186" data-mp="1zqhij8w6vwml" />
        </label>

        <label className="flex flex-col gap-1.5" data-id="element-187" data-mp="ebuwiknvvloj">
          <span className="text-small font-medium text-ink" data-id="element-188" data-mp="6tnb1whhs3ym">
            Last year <span className="text-ink-muted" data-id="element-189" data-mp="1vu3p04gd3wr">{end}</span>
          </span>
          <input type="range" min={FIRST_YEAR} max={LAST_YEAR} value={end} aria-label="Last year you were here" onChange={event => {
          const next = Number(event.target.value);
          onChange({
            start: Math.min(start, next),
            end: next
          });
        }} className="h-tap w-full accent-[color:var(--color-primary)]" data-id="element-190" data-mp="1xy7li2cq2x1" />
        </label>
      </div>

      <p aria-live="polite" className="text-small text-ink-muted" data-id="element-191" data-mp="3u0fub5pz99rc">
        {items > 0 ? `${items} archive ${items === 1 ? 'item' : 'items'} from ${start}–${end}.` : `No archive items from ${start}–${end} yet.`}
      </p>
    </div>;
}